// src/js/framework/globalState.ts
import type { IUser, ISite, IDevice, IRegister } from '../types/index.ts';

// ----------------------------------------------------------------
// Listener registry
// ----------------------------------------------------------------
type StateCallback = (value: unknown, oldValue?: unknown) => void;
const listeners = new Map<string, StateCallback[]>();

// ----------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------
function safeParse<T = unknown>(item: string, fallback: T | null = null): T | null {
  try {
    const raw = localStorage.getItem(item);
    if (raw && raw !== "undefined" && raw !== "null") {
      return JSON.parse(raw) as T;
    }
  } catch (err) {
    console.warn(`[globalState] Failed to parse ${item} from localStorage:`, err);
  }
  return fallback;
}

// ----------------------------------------------------------------
// State shape
// ----------------------------------------------------------------
export interface GlobalStateShape {
  user: IUser | null;
  token: string | null;
  currentSite: ISite | null;
  currentSiteWithData: (ISite & { devices?: IDevice[] }) | null;
  registers: IRegister[];
  devices: IDevice[];
  registerData: Record<string, number | string | boolean | null>;
  sld: unknown | null;
  sldDirty: boolean;
  // event helpers attached at runtime
  on: (event: string, callback: StateCallback) => () => void;
  off: (event: string, callback: StateCallback) => void;
  emit: (event: string, value?: unknown) => void;
  [key: string]: unknown;
}

function loadState(): Omit<GlobalStateShape, 'on' | 'off' | 'emit'> {
  return {
    user: safeParse<IUser>("user"),
    token: null,
    currentSite: safeParse<ISite>("currentSite"),
    currentSiteWithData: safeParse<ISite & { devices?: IDevice[] }>("currentSiteWithData"),
    registers: safeParse<IRegister[]>("registers") || [],
    devices: safeParse<IDevice[]>("devices") || [],
    registerData: {},
    sld: null,
    sldDirty: false,
  };
}

// ----------------------------------------------------------------
// Reactive proxy
// ----------------------------------------------------------------
export const globalState: GlobalStateShape = new Proxy(loadState() as GlobalStateShape, {
  set(target: GlobalStateShape, key: string, value: unknown): boolean {
    const oldValue = target[key];
    (target as Record<string, unknown>)[key] = value;

    // Persist certain keys
    if (key === "user") localStorage.setItem("user", JSON.stringify(value));
    if (key === "token") {
      localStorage.removeItem("token");
    }
    if (key === "currentSite") {
      if (value) localStorage.setItem("currentSite", JSON.stringify(value));
      else localStorage.removeItem("currentSite");
    }
    if (key === "currentSiteWithData") {
      if (value) localStorage.setItem("currentSiteWithData", JSON.stringify(value));
      else localStorage.removeItem("currentSiteWithData");
    }
    if (key === "registers") {
      if (Array.isArray(value)) localStorage.setItem("registers", JSON.stringify(value));
      else localStorage.removeItem("registers");
    }
    if (key === "devices") {
      if (Array.isArray(value)) localStorage.setItem("devices", JSON.stringify(value));
      else localStorage.removeItem("devices");
    }

    document.dispatchEvent(new CustomEvent("global:stateChange", {
      detail: { key, value, oldValue },
    }));

    if (key === "currentSite" || key === "currentSiteWithData" || key === "user" || key === "token") {
      document.dispatchEvent(new CustomEvent("funzi:state", {
        detail: { key, value, oldValue },
      }));
    }

    if (listeners.has(key as string)) {
      listeners.get(key as string)!.forEach(cb => cb(value, oldValue));
    }

    return true;
  },
});

// ----------------- Event Subscription API -----------------
globalState.on = function (event: string, callback: StateCallback): () => void {
  if (!listeners.has(event)) listeners.set(event, []);
  listeners.get(event)!.push(callback);
  return () => {
    const callbacks = listeners.get(event);
    if (callbacks) listeners.set(event, callbacks.filter(cb => cb !== callback));
  };
};

globalState.off = function (event: string, callback: StateCallback): void {
  const callbacks = listeners.get(event);
  if (callbacks) listeners.set(event, callbacks.filter(cb => cb !== callback));
};

globalState.emit = function (event: string, value?: unknown): void {
  const callbacks = listeners.get(event);
  if (callbacks) callbacks.forEach(cb => cb(value));
};
